import type { InferOutput } from "valibot";
import {
  array,
  boolean,
  literal,
  nonOptional,
  object,
  optional,
  partialCheck,
  picklist,
  pipe,
  string,
  variant,
} from "valibot";
import type { IncompleteIntegrationDefinition } from "../types";
import { initIntegrationDefinition } from "../Integration";
import { useTranslation } from "../../state/i18n";
import { email, errorTranslation, optionalTextValue } from "../../utils/validation/base";
import { TextInput } from "../../components/inputs/TextInput";
import {
  MAX_CHARACTERS_2XL,
  MAX_CHARACTERS_MD,
  MAX_CHARACTERS_SM,
  MAX_EXCEL_INTEGRATION_TEMPLATE_SIZE,
  MAX_TOTAL_RECIPIENTS,
  MIME_TYPE_EXCEL,
  MIME_TYPE_EXCEL_MACRO_ENABLED,
  FILE_EXTENSION_EXCEL_MACRO_ENABLED,
} from "../../constants";
import FileInput from "../../components/inputs/FileInput";
import { isNil } from "lodash-es";
import { createMemo, createSignal, getOwner, runWithOwner } from "solid-js";
import type { PendingCustomerResource } from "../../services/CustomerResourceService";
import CustomerResourceService from "../../services/CustomerResourceService";
import type { IconName } from "../../components/icon/Icon";
import { noop } from "../../utils/noop";
import type { FieldType, Nullish } from "../../types";
import { parseToRecipients } from "../integrationUtils";
import { triggerBrowserDownload } from "../../utils/uploadUtil";
import MixedInput from "../../components/inputs/MixedInput";
import { metaVariableOption, toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { useFormServices } from "../../services/formServices";
import { Checkbox } from "../../components/checkbox/Checkbox";
import Wysiwyg from "../../components/wysiwyg/Wysiwyg";
import { recipient } from "../../utils/validation/recipient";
import FieldValueReference from "../../fields/FieldValueReference";
import { parseStringTemplate, splitLiteralsOnComma, stringTemplateToString } from "../../utils/stringTemplateUtil";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import { insertBetween } from "../../utils/arrayUtil";
import { RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS } from "../../utils/fieldUtil";
import type { VariableType } from "../../variables/types";
import { RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES, toVariableValueOption } from "../../variables/variableUtil";
import type { VariableConfig } from "../../fields/types";
import { useClient } from "../../state/client";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import { SingleSelectInput } from "../../components/menu/Select";
import MetaVariableReference from "../../metaVariables/MetaVariableReference";
import VariableValueReference from "../../variables/VariableValueReference";
import type { MetaVariableId } from "../../metaVariables/metaVariable";
import { integrationsIcons } from "../IntegrationIcons";
import { keyFromSelector } from "i18next";

const ALLOWED_PLACEHOLDER_FIELDS: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "text",
  "textArea",
  "time",
  "timeDifference",
];

const ALLOWED_PLACEHOLDER_VARIABLES: VariableType[] = ["string", "number", "boolean", "date", "datetime"];

const ALLOWED_PLACEHOLDER_META_VARIABLES: MetaVariableId[] = [
  "info.userId",
  "meta.location.longitude",
  "meta.location.latitude",
  "meta.registrationDate",
  "meta.guid",
  "meta.taskId",
  "meta.serialNumber",
  "info.formName",
  "info.summary",
];

export type ExcelIntegrationDto = {
  template: Nullish<{
    filename: string;
    resourceId: string;
  }>;
  legacyDelimiters: boolean;
  filename: Nullish<string>;
  imageQuality: "original" | "high" | "medium" | "low";
  fromName: Nullish<string>;
  replyTo: Nullish<string>;
  recipients: Nullish<string>;
  dynamicRecipients?: string[];
  copyToUser: boolean;
  cc: Nullish<string>;
  bcc: Nullish<string>;
  subject: Nullish<string>;
  body: Nullish<string>;
};

const BaseSchema = {
  template: nonOptional(
    object({
      filename: string(),
      resourceId: string(),
    }),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY)),
  ),
  legacyDelimiters: boolean(),
  imageQuality: picklist(
    ["original", "high", "medium", "low"],
    errorTranslation(keyFromSelector(($) => $.VALIDATION_ENUM)),
  ),
  fromName: optionalTextValue(MAX_CHARACTERS_SM),
  replyTo: optional(email(MAX_CHARACTERS_SM)),
  recipients: array(recipient()),
  cc: array(recipient()),
  bcc: array(recipient()),
};

const Schema = pipe(
  variant("legacyDelimiters", [
    object({
      ...BaseSchema,
      legacyDelimiters: literal(true),
      filename: stringTemplate(
        ALLOWED_PLACEHOLDER_FIELDS,
        ALLOWED_PLACEHOLDER_VARIABLES,
        ALLOWED_PLACEHOLDER_META_VARIABLES,
        MAX_CHARACTERS_SM,
      ),
      subject: stringTemplate(
        ALLOWED_PLACEHOLDER_FIELDS,
        ALLOWED_PLACEHOLDER_VARIABLES,
        ALLOWED_PLACEHOLDER_META_VARIABLES,
        MAX_CHARACTERS_MD,
      ),
      body: stringTemplate(
        ALLOWED_PLACEHOLDER_FIELDS,
        ALLOWED_PLACEHOLDER_VARIABLES,
        ALLOWED_PLACEHOLDER_META_VARIABLES,
        MAX_CHARACTERS_2XL,
      ),
    }),
    object({
      ...BaseSchema,
      legacyDelimiters: literal(false),
      filename: stringTemplate(
        ALLOWED_PLACEHOLDER_FIELDS,
        ALLOWED_PLACEHOLDER_VARIABLES,
        ALLOWED_PLACEHOLDER_META_VARIABLES,
        MAX_CHARACTERS_SM,
        true,
        "{",
      ),
      subject: stringTemplate(
        ALLOWED_PLACEHOLDER_FIELDS,
        ALLOWED_PLACEHOLDER_VARIABLES,
        ALLOWED_PLACEHOLDER_META_VARIABLES,
        MAX_CHARACTERS_MD,
        true,
        "{",
      ),
      body: stringTemplate(
        ALLOWED_PLACEHOLDER_FIELDS,
        ALLOWED_PLACEHOLDER_VARIABLES,
        ALLOWED_PLACEHOLDER_META_VARIABLES,
        MAX_CHARACTERS_2XL,
        true,
        "{",
      ),
    }),
  ]),
  partialCheck(
    [["recipients"], ["cc"], ["bcc"]],
    ({ recipients, cc, bcc }) => recipients.length + cc.length + bcc.length <= MAX_TOTAL_RECIPIENTS,
    errorTranslation(
      keyFromSelector(($) => $.VALIDATION_MAX_TOTAL_RECIPIENTS),
      { max: MAX_TOTAL_RECIPIENTS },
    ),
  ),
);
const parseToMixedValuesWithDelimiter = (value: string, legacyDelimiters: boolean, variables: VariableConfig[]) =>
  parseToRecipients(value, variables, legacyDelimiters ? "${" : "{");

export type ExcelIntegrationConfiguration = InferOutput<typeof Schema>;

// This and the 'Word' integration are almost duplicates of each other. So if you make any changes, think about if you
// need to make them in both.
const ExcelIntegration: IncompleteIntegrationDefinition<ExcelIntegrationConfiguration, ExcelIntegrationDto> = {
  type: "excel",
  legacy: {
    namespace: "com.moreapps.plugins",
    key: "excel",
    version: 6,
  },
  schema: Schema,
  icon: integrationsIcons["excel"],
  titleKey: keyFromSelector(($) => $.EXCEL_INTEGRATION_TITLE),
  descriptionKey: keyFromSelector(($) => $.EXCEL_INTEGRATION_DESCRIPTION),
  verifiable: true,
  defaultProperties: (t) => ({
    template: undefined,
    legacyDelimiters: false,
    filename: parseStringTemplate(
      t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_FILENAME_DEFAULT_VALUE),
      [],
      [],
      "{",
    ),
    imageQuality: "high",
    recipients: [{ type: "metaVariable", value: MetaVariableReference.from("sys.user") }],
    cc: [],
    bcc: [],
    subject: parseStringTemplate(
      t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_SUBJECT_DEFAULT_VALUE),
      [],
      [],
      "{",
    ),
    body: parseStringTemplate(
      t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_BODY_DEFAULT_VALUE),
      [],
      [],
      "{",
    ),
  }),
  propertyKeys: {
    template: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_TEMPLATE_LABEL),
    legacyDelimiters: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_LEGACY_DELIMITERS_LABEL),
    filename: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_FILENAME_LABEL),
    imageQuality: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_IMAGE_QUALITY_LABEL),
    fromName: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_FROM_NAME_LABEL),
    replyTo: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_REPLY_TO_LABEL),
    recipients: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_RECIPIENTS_LABEL),
    cc: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_CC_LABEL),
    bcc: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_BCC_LABEL),
    subject: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_SUBJECT_LABEL),
    body: keyFromSelector(($) => $.EXCEL_INTEGRATION_CONFIGURATION_BODY_LABEL),
  },
  fromDto: (configuration, fields, variables) => {
    const placeholderDelimiter = configuration.legacyDelimiters ? "${" : "{";
    const recipients: ExcelIntegrationConfiguration["recipients"] = [];
    if (!isNil(configuration.recipients)) {
      recipients.push(
        ...parseToMixedValuesWithDelimiter(configuration.recipients, configuration.legacyDelimiters, variables),
      );
    }
    if (!isNil(configuration.dynamicRecipients)) {
      recipients.push(
        ...configuration.dynamicRecipients.map((r) =>
          variables.some((variable) => variable.uid.equals(r))
            ? { type: "variable" as const, value: VariableValueReference.from(r) }
            : { type: "field" as const, value: FieldValueReference.from(r) },
        ),
      );
    }
    if (configuration.copyToUser) {
      recipients.push({ type: "metaVariable", value: MetaVariableReference.from("sys.user") });
    }

    return {
      template: configuration.template ?? undefined,
      legacyDelimiters: configuration.legacyDelimiters,
      filename: parseStringTemplate(configuration.filename, fields, variables, placeholderDelimiter),
      imageQuality: configuration.imageQuality,
      fromName: configuration.fromName ?? undefined,
      replyTo: configuration.replyTo ?? undefined,
      recipients,
      cc: isNil(configuration.cc)
        ? []
        : splitLiteralsOnComma(parseStringTemplate(configuration.cc, fields, variables, placeholderDelimiter)),
      bcc: isNil(configuration.bcc)
        ? []
        : splitLiteralsOnComma(parseStringTemplate(configuration.bcc, fields, variables, placeholderDelimiter)),
      subject: parseStringTemplate(configuration.subject, fields, variables, placeholderDelimiter),
      body: parseStringTemplate(configuration.body, fields, variables, placeholderDelimiter),
    };
  },
  toDto: (configuration, fields, variables) => {
    const placeholderDelimiter = configuration.legacyDelimiters ? "${" : "{";
    const recipients = configuration.recipients
      .filter((r) => r.type === "literal")
      .map((r) => r.value)
      .join(",");
    const dynamicRecipients = configuration.recipients
      .filter((r) => r.type === "field" || r.type === "variable")
      .map((r) => r.value.toString());
    const copyToUser = !isNil(
      configuration.recipients.find(
        (r) => r.type === "metaVariable" && r.value.equals(MetaVariableReference.from("sys.user")),
      ),
    );

    return {
      template: configuration.template,
      legacyDelimiters: configuration.legacyDelimiters,
      filename: stringTemplateToString(configuration.filename, fields, variables, placeholderDelimiter),
      imageQuality: configuration.imageQuality,
      fromName: configuration.fromName,
      replyTo: configuration.replyTo,
      recipients,
      dynamicRecipients,
      copyToUser,
      cc: stringTemplateToString(configuration.cc, fields, variables, placeholderDelimiter, ","),
      bcc: stringTemplateToString(configuration.bcc, fields, variables, placeholderDelimiter, ","),
      subject: stringTemplateToString(configuration.subject, fields, variables, placeholderDelimiter),
      body: stringTemplateToString(configuration.body, fields, variables, placeholderDelimiter),
    };
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();
    const owner = getOwner();
    const template = createMemo(() =>
      isNil(props.integration.configuration.template)
        ? undefined
        : {
            filename: props.integration.configuration.template.filename,
            mediaTypeIcon: "excelLogo" as IconName,
          },
    );

    const [abortController, setAbortController] = createSignal<AbortController>();

    const placeholderDelimiter = createMemo(() => (props.integration.configuration.legacyDelimiters ? "${" : "{"));
    const placeholderMenuOptions = createMemo(() =>
      createDynamicValueOptions(t, {
        fields: fieldService
          .getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS)
          .flatMap((field) => toFieldValueOptions(field)),
        subforms: [],
        customVariables: variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES).map(toVariableValueOption),
        metaVariables: ALLOWED_PLACEHOLDER_META_VARIABLES.map(metaVariableOption),
      }),
    );

    const onFileSelected = async (file: PendingCustomerResource) => {
      const uploadedCustomerResource = await CustomerResourceService.upload(
        file,
        noop,
        setAbortController(new AbortController()).signal,
      );
      props.updateConfiguration("template", {
        filename: uploadedCustomerResource.fileName,
        resourceId: uploadedCustomerResource.id,
      });
    };

    return (
      <>
        <FileInput
          label={t(props.propertyKeys.template)}
          value={template()}
          maxFileSizeInBytes={MAX_EXCEL_INTEGRATION_TEMPLATE_SIZE}
          onFileSelected={onFileSelected}
          onUploadCancel={() => {
            abortController()?.abort();
          }}
          downloadFile={() => {
            const template = props.integration.configuration.template;
            if (!isNil(template)) {
              runWithOwner(owner, async () => {
                const client = useClient();
                const uri = await CustomerResourceService.getSecureUri(template.resourceId);
                await triggerBrowserDownload(client, uri, template.filename);
              });
            }
          }}
          errorMessage={props.getError("template")}
          required
          // Browsers may report an .xlsm file without a MIME type or as 'application/octet-stream', so match its extension as well
          accept={[MIME_TYPE_EXCEL, MIME_TYPE_EXCEL_MACRO_ENABLED, FILE_EXTENSION_EXCEL_MACRO_ENABLED]}
          uploadActionLabel={t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_TEMPLATE_UPLOAD_LABEL)}
          info={{
            title: t(props.propertyKeys.template),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.template }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_TEMPLATE_DESCRIPTION),
          }}
        />

        <Checkbox
          block
          label={t(props.propertyKeys.legacyDelimiters)}
          checked={props.integration.configuration.legacyDelimiters}
          setChecked={(value) => {
            props.updateConfiguration("legacyDelimiters", value);
          }}
          info={{
            title: t(props.propertyKeys.legacyDelimiters),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.legacyDelimiters,
            }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_LEGACY_DELIMITERS_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.filename)}
          value={props.integration.configuration.filename}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={placeholderMenuOptions()}
          onChange={(value) => {
            props.updateConfiguration("filename", value);
          }}
          errorMessage={props.getError("filename")}
          required
          info={{
            title: t(props.propertyKeys.filename),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.filename }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_FROM_NAME_DESCRIPTION),
          }}
          delimiter={placeholderDelimiter()}
        />

        <SingleSelectInput
          label={t(props.propertyKeys.imageQuality)}
          value={props.integration.configuration.imageQuality}
          onSelect={(value) => props.updateConfiguration("imageQuality", value)}
          options={[
            {
              variant: "text",
              value: "original",
              label: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_IMAGE_QUALITY_OPTIONS_ORIGINAL_LABEL),
            },
            {
              variant: "text",
              value: "high",
              label: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_IMAGE_QUALITY_OPTIONS_HIGH_LABEL),
            },
            {
              variant: "text",
              value: "medium",
              label: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_IMAGE_QUALITY_OPTIONS_MEDIUM_LABEL),
            },
            {
              variant: "text",
              value: "low",
              label: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_IMAGE_QUALITY_OPTIONS_LOW_LABEL),
            },
          ]}
          errorMessage={props.getError("imageQuality")}
          info={{
            title: t(props.propertyKeys.imageQuality),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
              property: props.propertyKeys.imageQuality,
            }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_IMAGE_QUALITY_DESCRIPTION),
          }}
          sameWidth
        />

        <TextInput
          label={t(props.propertyKeys.fromName)}
          placeholder={t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_FROM_NAME_PLACEHOLDER)}
          value={props.integration.configuration.fromName}
          onBlur={(e) => {
            props.updateConfiguration("fromName", e.target.value);
          }}
          errorMessage={props.getError("fromName")}
          info={{
            title: t(props.propertyKeys.fromName),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.fromName }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_FROM_NAME_DESCRIPTION),
          }}
        />

        {/* TODO - DEV-5952 Replace with 'SingleSelectInput' when 'creation' support has been added */}
        <TextInput
          label={t(props.propertyKeys.replyTo)}
          placeholder={t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_REPLY_TO_PLACEHOLDER)}
          value={props.integration.configuration.replyTo ?? ""}
          onBlur={(e) => {
            props.updateConfiguration("replyTo", e.target.value);
          }}
          errorMessage={props.getError("replyTo")}
          info={{
            title: t(props.propertyKeys.replyTo),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.replyTo }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_REPLY_TO_DESCRIPTION),
          }}
        />

        <MixedInput
          label={t(props.propertyKeys.recipients)}
          value={insertBetween(props.integration.configuration.recipients, { type: "literal", value: ", " })}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: [metaVariableOption("sys.user")],
          })}
          onChange={(value) => {
            props.updateConfiguration("recipients", splitLiteralsOnComma(value));
          }}
          errorMessage={props.getError("recipients")}
          info={{
            title: t(props.propertyKeys.recipients),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.recipients }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_RECIPIENTS_DESCRIPTION),
          }}
          delimiter={placeholderDelimiter()}
        />

        <MixedInput
          label={t(props.propertyKeys.cc)}
          placeholder={t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_CC_PLACEHOLDER)}
          value={insertBetween(props.integration.configuration.cc, { type: "literal", value: ", " })}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: [metaVariableOption("sys.user")],
          })}
          onChange={(value) => {
            props.updateConfiguration("cc", splitLiteralsOnComma(value));
          }}
          errorMessage={props.getError("cc")}
          info={{
            title: t(props.propertyKeys.cc),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.cc }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_CC_DESCRIPTION),
          }}
          delimiter={placeholderDelimiter()}
        />

        <MixedInput
          label={t(props.propertyKeys.bcc)}
          placeholder={t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_BCC_PLACEHOLDER)}
          value={insertBetween(props.integration.configuration.bcc, { type: "literal", value: ", " })}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={createDynamicValueOptions(t, {
            fields: fieldService
              .getFieldsByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_FIELDS)
              .flatMap((field) => toFieldValueOptions(field)),
            subforms: [],
            customVariables: variableService
              .getVariablesByTypes(RECIPIENT_ALLOWED_PLACEHOLDER_VARIABLES)
              .map(toVariableValueOption),
            metaVariables: [metaVariableOption("sys.user")],
          })}
          onChange={(value) => {
            props.updateConfiguration("bcc", splitLiteralsOnComma(value));
          }}
          errorMessage={props.getError("bcc")}
          info={{
            title: t(props.propertyKeys.bcc),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.bcc }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_BCC_DESCRIPTION),
          }}
          delimiter={placeholderDelimiter()}
        />

        <MixedInput
          label={t(props.propertyKeys.subject)}
          value={props.integration.configuration.subject}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES),
            )
          }
          options={placeholderMenuOptions()}
          onChange={(value) => {
            props.updateConfiguration("subject", value);
          }}
          errorMessage={props.getError("subject")}
          required
          info={{
            title: t(props.propertyKeys.subject),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.subject }),
            description: t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_SUBJECT_DESCRIPTION),
          }}
          delimiter={placeholderDelimiter()}
        />

        <Wysiwyg
          ariaLabel={t(($) => $.EXCEL_INTEGRATION_CONFIGURATION_BODY_LABEL)}
          content={props.integration.configuration.body ?? ""}
          onChange={(value) => props.updateConfiguration("body", value)}
          errorMessage={props.getError("body")}
          placeholderMenuOptions={placeholderMenuOptions()}
          placeholderDelimiter={placeholderDelimiter()}
          parse={(value) =>
            parseStringTemplate(
              value,
              fieldService.getFieldsByTypes(ALLOWED_PLACEHOLDER_FIELDS),
              variableService.getVariablesByTypes(ALLOWED_PLACEHOLDER_VARIABLES),
              placeholderDelimiter(),
            )
          }
        />
      </>
    );
  },
};

export default initIntegrationDefinition(ExcelIntegration);
